"use client";
import useTranslate from "@app/_hooks/useTranslate";

import Flag from "@components/Flag";
import { Input, Select, Textarea } from "@components/inputs";
import { Button } from "@components/ngg";
import { useForm, ValidationError } from "@formspree/react";
import type { Form, Maybe } from "@server/gql/graphql";

import { marketConfigurations } from "@/config/market-configurations";
import type { MarketEnum } from "@/config/markets/const";
import { cn } from "@/lib/utils";
import { useCallback } from "react";

const SelectIcon = ({ id }: { id: MarketEnum }) => (
  <Flag code={id} className="h-full w-full rounded-full object-cover" />
);

const formattedMarkets = Object.values(marketConfigurations)
  .filter((m) => m.code !== "eu")
  .map((m) => ({
    id: m.code,
    value: m.code,
    label: m.label,
  }));

export default function PlaceOrder({
  className,
  reference,
}: {
  className?: string;
  reference: string;
}) {
  const t = useTranslate();
  const [state, handleSubmit] = useForm(reference);
  const isInputError = useCallback(
    (inputName: string) => {
      if (!state.errors) return false;
      if (Object.values(state.errors).length === 0) return false;
      return state.errors.getFieldErrors(inputName).length > 0;
    },
    [state.errors],
  );

  if (state.succeeded) {
    return (
      <div className={className}>
        <h2 className="text-lg">
          {t("placeOrderFormSuccessHeading") ||
            t("formSuccessHeading")}
        </h2>
        <p className="mt-4 text-base">
          {t("placeOrderFormSuccessText") || t("formSuccessText")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("flex flex-col gap-y-6", className)}
    >
      <h2 className="w-full text-lg font-normal">
        {t("placeOrderFormHeadingPartOne")}
        <br />
        {t("placeOrderFormHeadingPartTwo")}
      </h2>

      <div className="space-y-3">
        <Input
          id="name"
          name="name"
          type="text"
          required
          fullWidth
          label={t("placeOrderFormNameLabel") || t("name")}
          classNames={{
            input: isInputError("name") ? "border border-error" : "",
          }}
        />
        <ValidationError
          className="mt-2 text-base"
          field="name"
          errors={state.errors}
        />

        <Select
          classNames={{
            button: "h-10 lg:h-10",
          }}
          name="shippingCountry"
          placeholder={
            t("placeOrderFormSelectShippingCountryPlaceholder") ||
            t("selectCountry")
          }
          options={formattedMarkets}
          required
          labelIcon={SelectIcon}
          optionIcon={SelectIcon}
        >
          <ValidationError
            className="mt-2 text-base"
            field="shippingCountry"
            errors={state.errors}
          />
        </Select>

        <Input
          id="email"
          name="email"
          type="email"
          required
          fullWidth
          label={t("placeOrderFormEmailLabel") || t("email")}
          classNames={{
            input: isInputError("email") ? "border border-error" : "",
          }}
        />
        <ValidationError
          className="mt-2 text-base"
          field="email"
          errors={state.errors}
        />

        <Textarea
          name="message"
          id="message"
          label={t("placeOrderFormMessageLabel") || t("message")}
          fullWidth
          resizable="none"
          classNames={{
            textarea: isInputError("message") ? "border border-error" : "",
          }}
        />
        <ValidationError
          className="mt-2 text-base"
          field="message"
          errors={state.errors}
        />
      </div>

      <Button
        type="submit"
        isLoading={state.submitting}
        fullWidth
        className="btn-primary"
      >
        {t("placeOrderFormSubmitButtonLabel") || t("send")}
      </Button>

      {/* Honeypot */}
      <input type="text" name="_gotcha" className="hidden" />
    </form>
  );
}

export function PlaceOrderPopup({ form }: { form?: Maybe<Form> }) {
  
  return (
    <PlaceOrder
      className="mx-auto w-full max-w-[420px] bg-white px-4 py-10 pt-12 sm:min-w-[420px] md:px-6"
      reference={form?.reference as string}
    />
  );
}
